// pages/PeApplyAdminPanel.js
// Admin → PE Apply: the nightly "apply DI decisions to Practice Engine" run.
//   • Status card — what a run would send right now (GET /api/diPeApplyAdmin `preview`).
//   • Dry run / Apply now — POST { action }. The request is held open until the run ends (can take
//     minutes), so it is never timed out client-side; only those two buttons are disabled meanwhile.
//   • Recent runs — click one for its summary (failures + manual items) and, on demand, the per-step
//     write log (GET ?runId=N).
// Backend: api/diPeApplyAdmin (real admins only).

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import './PeApplyAdminPanel.css';

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' }) : '—');
const fmtN = (n) => (n == null ? '—' : Number(n).toLocaleString());

async function api(method, body, qs = '') {
  const r = await fetch(`/api/diPeApplyAdmin${qs}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await r.text();
  let j = null;
  try { j = text ? JSON.parse(text) : null; } catch { /* non-JSON error body */ }
  if (!r.ok) throw new Error((j && j.error) || text || `HTTP ${r.status}`);
  return j;
}

function StatusBadge({ status }) {
  const s = status || 'running';
  return <span className={`pa-chip pa-chip--${s}`}>{s}</span>;
}

function Flag({ on, children }) {
  return <span className={`pa-flag ${on ? 'pa-flag--on' : 'pa-flag--off'}`}>{children}</span>;
}

// "wrote / ok / failed" cell for jobs or clients.
function CountCell({ c }) {
  if (!c) return <>—</>;
  return (
    <>
      {fmtN(c.ok)} ok{c.failed ? <span className="pa-bad"> · {fmtN(c.failed)} failed</span> : null}
      <span className="pa-sub">{fmtN(c.wrote)} wrote · {fmtN(c.planned)} planned</span>
    </>
  );
}

function pretty(json) {
  if (json == null || json === '') return '';
  try { return JSON.stringify(typeof json === 'string' ? JSON.parse(json) : json, null, 2); }
  catch { return String(json); }
}

function RunLog({ runId }) {
  const [log, setLog] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    api('GET', null, `?runId=${encodeURIComponent(runId)}`)
      .then(j => { if (alive) setLog(j.log || []); })
      .catch(e => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [runId]);

  if (err) return <div className="admin-flash error">Couldn't load the log ({err}).</div>;
  if (!log) return <p className="admin-hint">Loading log…</p>;
  if (log.length === 0) return <p className="admin-hint">No steps were logged for this run.</p>;
  return (
    <div className="pa-log">
      <table className="admin-table pa-table">
        <thead><tr><th>Time</th><th>Entity</th><th>Step</th><th>Status</th><th>Error</th><th>Detail</th></tr></thead>
        <tbody>
          {log.map(l => {
            const payload = pretty(l.payload_json);
            const detail = pretty(l.detail_json);
            return (
              <tr key={l.id}>
                <td className="pa-nowrap">{fmtTime(l.created_at)}</td>
                <td className="pa-nowrap">{l.entity_type} {l.entity_id}</td>
                <td>{l.step}</td>
                <td><span className={`pa-chip pa-chip--${String(l.status).toLowerCase()}`}>{l.status}</span></td>
                <td className="pa-bad pa-wrap">{l.error || ''}</td>
                <td>
                  {(payload || detail) ? (
                    <details>
                      <summary>JSON</summary>
                      {payload && <><div className="pa-sub">payload</div><pre className="pa-json">{payload}</pre></>}
                      {detail && <><div className="pa-sub">detail</div><pre className="pa-json">{detail}</pre></>}
                    </details>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SummaryBody({ run, summary, showLog, onToggleLog }) {
  const failures = (summary && summary.failures) || [];
  const manual = (summary && summary.manual) || [];
  const warnings = (summary && summary.warnings) || [];
  return (
    <div className="pa-detail">
      {summary && summary.error && <div className="admin-flash error">Run error: {summary.error}</div>}
      {!summary && <p className="admin-hint">No summary recorded{run && !run.finished_at ? ' — this run has not finished (or was interrupted)' : ''}.</p>}
      {summary && summary.nothingToDo && <p className="admin-hint">Nothing was queued.</p>}
      {warnings.length > 0 && (
        <div className="admin-flash error">
          <strong>Warnings</strong>
          <ul className="pa-warnings">{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </div>
      )}

      {failures.length > 0 && (
        <>
          <h5>Failures ({failures.length})</h5>
          <table className="admin-table pa-table">
            <thead><tr><th>Type</th><th>Id</th><th>Notes</th></tr></thead>
            <tbody>
              {failures.map((f, i) => (
                <tr key={i}>
                  <td>{f.type}</td>
                  <td className="pa-nowrap">{f.id}{f.code ? ` (${f.code})` : ''}</td>
                  <td className="pa-wrap">{(f.notes || []).join(' · ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {manual.length > 0 && (
        <>
          <h5>Manual — update in PE ({summary.manualTotal || manual.length})</h5>
          <p className="admin-hint">
            DI recorded these decisions but PE's API can't take them — someone needs to update them in PE by hand.
            {summary.manualTotal > manual.length ? ` Showing the first ${manual.length}.` : ''}
          </p>
          <table className="admin-table pa-table">
            <thead><tr><th>Type</th><th>Id / code</th><th>Reason</th></tr></thead>
            <tbody>
              {manual.map((m, i) => (
                <tr key={i}>
                  <td>{m.type}</td>
                  <td className="pa-nowrap">{m.id}{m.code ? ` (${m.code})` : ''}</td>
                  <td className="pa-wrap">{m.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {summary && !summary.nothingToDo && failures.length === 0 && manual.length === 0 && !summary.error && (
        <p className="admin-hint">No failures and nothing needing manual action.</p>
      )}

      <div className="pa-detail-foot">
        <button type="button" className="admin-btn admin-btn--secondary" onClick={onToggleLog}>
          {showLog ? 'Hide log' : 'Show log'}
        </button>
      </div>
      {showLog && run && <RunLog runId={run.id} />}
    </div>
  );
}

export default function PeApplyAdminPanel({ flash }) {
  const [view, setView] = useState(null);       // { preview, runs }
  const [err, setErr] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);   // last returned summary
  const [openRun, setOpenRun] = useState(null); // run id expanded
  const [logFor, setLogFor] = useState(null);   // run id whose log is shown
  const alive = useRef(true);

  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const load = useCallback(async () => {
    try {
      const j = await api('GET');
      if (alive.current) { setView(j); setErr(null); }
    } catch (e) {
      if (alive.current) setErr(e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function start() {
    const preview = view && view.preview;
    const ok = window.confirm(
      'Apply now WRITES to live Practice Engine.\n\n'
      + `It will send everything currently waiting${preview ? ` (${preview.jobs} jobs, ${preview.clients} clients)` : ''}, `
      + 'and the request stays open until the run finishes (it can take several minutes — keep this tab open).\n\n'
      + 'Continue?');
    if (!ok) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await api('POST', { action: 'run' });
      if (!alive.current) return;
      setResult(r);
      if (r.runId != null) setOpenRun(r.runId);
      flash && flash(r.nothingToDo ? 'Apply finished: nothing was queued.' : `Apply finished: ${r.status}.`, r.status === 'failed');
    } catch (e) {
      if (alive.current) setResult({ requestError: e.message });
      flash && flash(`Apply didn't run: ${e.message}`, true);
    } finally {
      if (alive.current) setRunning(false);
      load();
    }
  }

  if (!view) {
    return (
      <div className="admin-panel">
        <h3>PE Apply</h3>
        {err ? <div className="admin-flash error">Couldn't load ({err}).</div> : <p className="admin-hint">Loading…</p>}
      </div>
    );
  }

  const p = view.preview || {};
  const runs = view.runs || [];

  return (
    <div className="admin-panel pa-panel">
      <h3>PE Apply</h3>
      <p className="admin-hint">
        Every night DI consolidates all unapplied decisions into one change set per job / client and writes it to
        Practice Engine, logging every attempt. Use this page to preview the queue, run it on demand, and review what happened.
      </p>

      {err && <div className="admin-flash error">Last reload failed ({err}) — showing the previous status.</div>}
      {p.configured === false && (
        <div className="admin-flash error">
          PE write credentials are not configured. Runs with something queued will fail until
          PE_WRITE_CLIENT_ID / PE_WRITE_CLIENT_SECRET are set.
        </div>
      )}
      {(p.warnings || []).length > 0 && (
        <div className="admin-flash error">
          <strong>Warnings</strong>
          <ul className="pa-warnings">{p.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </div>
      )}

      {/* ── Status ───────────────────────────────────────────────────────── */}
      <h4>Waiting to be applied</h4>
      <div className="pa-card">
        <div className="pa-stats">
          <div className="pa-stat"><span className="pa-stat-n">{fmtN(p.jobs)}</span><span className="pa-stat-l">jobs</span></div>
          <div className="pa-stat"><span className="pa-stat-n">{fmtN(p.clients)}</span><span className="pa-stat-l">clients</span></div>
          <div className="pa-stat" title="Decisions DI holds that PE's API can't take — someone must update them in PE by hand.">
            <span className="pa-stat-n">{fmtN(p.manual)}</span><span className="pa-stat-l">manual items</span>
          </div>
        </div>
        <div className="pa-flags">
          <Flag on={p.configured}>{p.configured ? 'PE credentials configured' : 'PE credentials missing'}</Flag>
          {p.datesEnabled
            ? <Flag on>{`Expected-completion date written to ${p.expectedField || 'PE'}`}</Flag>
            : <span className="pa-flag pa-flag--info">Expected-completion date not written</span>}
        </div>
        {p.steps && (
          <table className="admin-table pa-table pa-steps">
            <thead>
              <tr>
                <th colSpan={4}>Jobs</th><th colSpan={2}>Clients</th>
              </tr>
              <tr>
                <th>Roll decision</th><th>Panel update</th><th>Dates</th><th>Note</th>
                <th>Attributes</th><th>Partner / Manager</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{fmtN(p.steps.roll)}</td><td>{fmtN(p.steps.panel)}</td><td>{fmtN(p.steps.dates)}</td><td>{fmtN(p.steps.note)}</td>
                <td>{fmtN(p.steps.clientAttrs)}</td><td>{fmtN(p.steps.clientStaff)}</td>
              </tr>
            </tbody>
          </table>
        )}

        {(p.sample || []).length > 0 && (
          <details className="pa-sample">
            <summary>Sample of waiting jobs (first {(p.sample || []).length})</summary>
            <table className="admin-table pa-table">
              <thead><tr><th>Job</th><th>Steps</th><th>Sections</th></tr></thead>
              <tbody>
                {p.sample.map(s => (
                  <tr key={s.jobIdx}>
                    <td className="pa-nowrap">{s.jobIdx}</td>
                    <td>{(s.steps || []).map(st => <span key={st} className="pa-tag">{st}</span>)}</td>
                    <td>{(s.sections || []).map(st => <span key={st} className="pa-tag">{st}</span>)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}

        <div className="pa-actions">
          <button type="button" className="admin-btn" disabled={running} onClick={start}>
            {running ? 'Applying to PE…' : 'Apply now'}
          </button>
          <button type="button" className="dr-linkbtn pa-reload" onClick={load}>Reload status</button>
        </div>
        {running && (
          <p className="admin-hint pa-progress">
            <span className="admin-spinner" aria-hidden="true" /> Writing to PE — this request stays open until the run
            finishes and can take several minutes. Keep this tab open.
          </p>
        )}
      </div>

      {/* ── Last result ──────────────────────────────────────────────────── */}
      {result && result.requestError && (
        <div className="admin-flash error">Run did not start or its answer was lost: {result.requestError}. Check Recent runs below — it may still have run.</div>
      )}
      {result && !result.requestError && (
        <>
          <h4>Result of this run</h4>
          <div className="pa-card">
            <div className="pa-result-head">
              <StatusBadge status={result.status} />
              <span className="pa-sub">run #{result.runId}</span>
            </div>
            {!result.nothingToDo && (
              <table className="admin-table pa-table">
                <thead><tr><th /><th>Planned</th><th>OK</th><th>Failed</th><th>Wrote to PE</th></tr></thead>
                <tbody>
                  {['jobs', 'clients'].map(k => (
                    <tr key={k}>
                      <td>{k}</td>
                      <td>{fmtN(result[k] && result[k].planned)}</td>
                      <td>{fmtN(result[k] && result[k].ok)}</td>
                      <td className={result[k] && result[k].failed ? 'pa-bad' : undefined}>{fmtN(result[k] && result[k].failed)}</td>
                      <td>{fmtN(result[k] && result[k].wrote)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <SummaryBody
              run={{ id: result.runId, finished_at: true }}
              summary={result}
              showLog={logFor === result.runId}
              onToggleLog={() => setLogFor(logFor === result.runId ? null : result.runId)}
            />
          </div>
        </>
      )}

      {/* ── Recent runs ──────────────────────────────────────────────────── */}
      <h4>Recent runs</h4>
      {runs.length === 0 ? <p className="admin-hint">No runs yet.</p> : (
        <table className="admin-table pa-table pa-runs">
          <thead>
            <tr>
              <th style={{ width: 26 }} />
              <th>Started</th><th>Who</th><th>Status</th>
              <th>Jobs</th><th>Clients</th>
            </tr>
          </thead>
          <tbody>
            {runs.map(r => {
              const s = r.summary;
              const expanded = openRun === r.id;
              return (
                <Fragment key={r.id}>
                  <tr className={`pa-run-row ${expanded ? 'expanded' : ''}`} onClick={() => setOpenRun(expanded ? null : r.id)}>
                    <td className="admin-twisty">{expanded ? '▾' : '▸'}</td>
                    <td className="pa-nowrap">{fmtTime(r.started_at)}{r.finished_at ? null : <span className="pa-sub">not finished</span>}</td>
                    <td>{r.triggered_by === 'schedule' ? 'Nightly schedule' : r.triggered_by}</td>
                    <td><StatusBadge status={r.status} /></td>
                    <td>{s && s.nothingToDo ? <span className="pa-sub">Nothing queued</span> : <CountCell c={s && s.jobs} />}</td>
                    <td>{s && s.nothingToDo ? '' : <CountCell c={s && s.clients} />}</td>
                  </tr>
                  {expanded && (
                    <tr className="admin-edit-row">
                      <td />
                      <td colSpan={5}>
                        <SummaryBody
                          run={r}
                          summary={s}
                          showLog={logFor === r.id}
                          onToggleLog={() => setLogFor(logFor === r.id ? null : r.id)}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
